// This file is auto-generated by @hey-api/openapi-ts

import type { Client, Options as Options2, TDataShape } from "./client";
import { client } from "./client.gen";
import type {
  GetDiagnosticConfigData,
  GetDiagnosticConfigResponses,
  GetDiagnosticQuestionsData,
  GetDiagnosticQuestionsResponses,
  GetHealthData,
  GetHealthResponses,
  ScoreDiagnosticAnswerData,
  ScoreDiagnosticAnswerErrors,
  ScoreDiagnosticAnswerResponses,
  WriteDiagnosticReportData,
  WriteDiagnosticReportErrors,
  WriteDiagnosticReportResponses,
} from "./types.gen";

export type Options<
  TData extends TDataShape = TDataShape,
  ThrowOnError extends boolean = boolean,
  TResponse = unknown,
> = Options2<TData, ThrowOnError, TResponse> & {
  /**
   * You can provide a client instance returned by `createClient()` instead of
   * individual options. This might be also useful if you want to implement a
   * custom client.
   */
  client?: Client;
  /**
   * You can pass arbitrary values through the `meta` object. This can be
   * used to access values that aren't defined as part of the SDK function.
   */
  meta?: Record<string, unknown>;
};

/**
 * Diagnostic Config
 *
 * Return the public runtime config: GA measurement ID and HubSpot tracking settings.
 */
export const getDiagnosticConfig = <ThrowOnError extends boolean = false>(
  options?: Options<GetDiagnosticConfigData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetDiagnosticConfigResponses, unknown, ThrowOnError>({
    url: "/diagnostic/config",
    ...options,
  });

/**
 * Diagnostic Questions
 *
 * Return the questions, chips, scores, reflections and scoring rules as data.
 */
export const getDiagnosticQuestions = <ThrowOnError extends boolean = false>(
  options?: Options<GetDiagnosticQuestionsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetDiagnosticQuestionsResponses, unknown, ThrowOnError>({
    url: "/diagnostic/questions",
    ...options,
  });

/**
 * Diagnostic Score
 *
 * Score one answer. Chips score locally; typed text is sharpened by the model.
 *
 * Chips alone never reach the model, so tapping through the interview costs
 * no network wait. Typed text goes to the fast model with the chip score as
 * its anchor; if that call fails the chip score stands, and with no chip the
 * keyword heuristic does.
 *
 * Raises:
 * HTTPException: 404 if the question id is unknown, 400 if a chip label
 * is not one of that question's options.
 */
export const scoreDiagnosticAnswer = <ThrowOnError extends boolean = false>(
  options: Options<ScoreDiagnosticAnswerData, ThrowOnError>,
) =>
  (options.client ?? client).post<
    ScoreDiagnosticAnswerResponses,
    ScoreDiagnosticAnswerErrors,
    ThrowOnError
  >({
    url: "/diagnostic/score",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Diagnostic Report
 *
 * Write the diagnostic, then queue the lead for HubSpot. Scores are recomputed here.
 *
 * The lead is built from the written report, because the report is one of
 * its fields (HubSpot's results email shows it). It is sent after the
 * response as a background task, so the visitor waits only for the model,
 * and a HubSpot outage never holds back a report that is already written.
 *
 * Raises:
 * HTTPException: 400 if consent was not given, if an answer names an
 * unknown question, repeats one, or carries a chip label that
 * question does not offer.
 */
export const writeDiagnosticReport = <ThrowOnError extends boolean = false>(
  options: Options<WriteDiagnosticReportData, ThrowOnError>,
) =>
  (options.client ?? client).post<
    WriteDiagnosticReportResponses,
    WriteDiagnosticReportErrors,
    ThrowOnError
  >({
    url: "/diagnostic/report",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Health
 *
 * Return a simple health status payload.
 */
export const getHealth = <ThrowOnError extends boolean = false>(
  options?: Options<GetHealthData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetHealthResponses, unknown, ThrowOnError>({
    url: "/health",
    ...options,
  });
